import React, { useEffect, useState } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, CheckCircle, Target } from 'lucide-react';

interface AccuracyData {
  answered: number;
  correct: number;
  pct_correct: number;
}

interface PredictionAccuracyCardProps {
  session: Session | null;
}

const EMPTY_ACCURACY: AccuracyData = {
  answered: 0,
  correct: 0,
  pct_correct: 0,
};

export const PredictionAccuracyCard: React.FC<PredictionAccuracyCardProps> = ({ session }) => {
  const [data, setData] = useState<AccuracyData>(EMPTY_ACCURACY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const fetchAccuracy = async () => {
      setLoading(true);

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = session?.access_token ?? sessionData.session?.access_token;

        if (!accessToken) {
          if (active) setData(EMPTY_ACCURACY);
          return;
        }

        const { data: responseData, error } = await supabase.functions.invoke('user-accuracy', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (error) throw error;

        const row = responseData?.data?.[0];
        if (active) {
          setData({
            answered: Number(row?.answered ?? 0),
            correct: Number(row?.correct ?? 0),
            pct_correct: Number(row?.pct_correct ?? 0),
          });
        }
      } catch {
        // Fallback: read via RLS directly if edge function is unavailable
        try {
          const { data: predictions, error: fallbackError } = await supabase
            .from('user_predictions')
            .select('is_correct')
            .not('is_correct', 'is', null);

          if (fallbackError) throw fallbackError;

          const answered = predictions?.length ?? 0;
          const correct = predictions?.filter((p) => p.is_correct === true).length ?? 0;
          const pctCorrect = answered > 0 ? Math.round((correct / answered) * 1000) / 10 : 0;

          if (active) {
            setData({
              answered,
              correct,
              pct_correct: pctCorrect,
            });
          }
        } catch {
          if (active) setData(EMPTY_ACCURACY);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchAccuracy();

    return () => {
      active = false;
    };
  }, [session?.access_token]);

  if (loading) {
    return (
      <Card className="w-full sm:w-auto p-4 flex items-center gap-6 border-primary/20 bg-primary/5">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-20" />
      </Card>
    );
  }

  return (
    <Card className="w-full sm:w-auto p-4 flex flex-wrap items-center gap-4 border-primary/20 bg-primary/5">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <TrendingUp className="h-4 w-4 text-primary" />
        <span>Prediction Accuracy</span>
      </div>

      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Target className="h-3.5 w-3.5" />
        <span>
          <strong className="text-foreground">{data.answered}</strong> answered
        </span>
      </div>

      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <CheckCircle className="h-3.5 w-3.5 text-success" />
        <span>
          <strong className="text-foreground">{data.correct}</strong> correct
        </span>
      </div>

      <div className="text-base font-semibold text-primary">{data.pct_correct}%</div>
    </Card>
  );
};

export default PredictionAccuracyCard;
